import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { lookupsJson } from '@ourbranch/lookups';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { LabelTooltip } from 'core/components/label-tooltip';
import { RenovationFieldsForm } from 'common/components/farm/renovation-fields-form';

import useStyles from './barndominium-dwelling-form.styles';

const basementTypeOptions = lookupsJson.basementType;
const exteriorWallTypeOptions = lookupsJson.exteriorWallType;
const fireProtectiveDevicesOptions = lookupsJson.farm.fireProtectiveDevice;
const heatingSourceOptions = lookupsJson.farm.primaryHeatingSourceOptions;
const homeQualityOptions = lookupsJson.homeQuality;
const roofShapeOptions = lookupsJson.roofShape;
const roofTypeOptions = lookupsJson.farm.dwellingRoofType;
const theftPreventionDevicesOptions = lookupsJson.farm.theftProtectiveDevice;
const constructionTypeOptions = lookupsJson.farm.dwellingConstructionType;
const garageTypeOptions = lookupsJson.garageType;
const numFamiliesInDwellingOptions = lookupsJson.farm.numFamiliesInDwellingOptions;

const BarndomininumForm = ({
  basePath,
  isSelectedSecondaryHeatingSourceUnitCountBased,
  disableRiskForm,
  initialBuilding,
  initialConnectedHome,
  toastRCEWarning,
  setFieldValue
}) => {
  const classes = useStyles();
  // Set default values for ownership and home type for new risks only
  useEffect(() => {
    if (setFieldValue && basePath) {
      // Only set defaults if values don't already exist (new risk)
      if (!initialBuilding?.ownershipStatus) {
        setFieldValue(`${basePath}.building.ownershipStatus`, 'P');
      }
      if (!initialBuilding?.typeOfHome) {
        setFieldValue(`${basePath}.building.typeOfHome`, '1');
      }
    }
  }, [basePath, setFieldValue, initialBuilding?.ownershipStatus, initialBuilding?.typeOfHome]);

  return (
    <>
      <Grid item xs={12}>
        <Label type="infoTitleSmall">Property</Label>
      </Grid>
      <Field
        xs={4}
        name={`${basePath}.building.yearBuilt`}
        id={`${basePath}.building.yearBuilt`}
        type="numeric"
        label="Year built in*"
        mode="light"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.yearBuilt) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={4}
        type="numeric"
        id={`${basePath}.building.sqFt`}
        name={`${basePath}.building.sqFt`}
        mode="light"
        label="Above-ground sq ft*"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.sqFt) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={4}
        type="numeric"
        id={`${basePath}.building.basementSqFt`}
        name={`${basePath}.building.basementSqFt`}
        mode="light"
        label="Basement sq ft*"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.basementSqFt) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={6}
        type="select"
        options={numFamiliesInDwellingOptions}
        id={`${basePath}.building.numFamiliesInDwelling`}
        name={`${basePath}.building.numFamiliesInDwelling`}
        mode="light"
        label="Families in dwelling*"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.numFamiliesInDwelling) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={6}
        type="select"
        options={homeQualityOptions}
        id={`${basePath}.building.homeQuality`}
        name={`${basePath}.building.homeQuality`}
        mode="light"
        label="Home quality*"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.homeQuality) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={3}
        type="numeric"
        id={`${basePath}.building.numStories`}
        name={`${basePath}.building.numStories`}
        mode="light"
        label="Stories*"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.numStories) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={3}
        type="numeric"
        id={`${basePath}.building.numHalfBathrooms`}
        name={`${basePath}.building.numHalfBathrooms`}
        mode="light"
        label="Half bathrooms*"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.numHalfBathrooms) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={3}
        type="numeric"
        id={`${basePath}.building.numFullBathrooms`}
        name={`${basePath}.building.numFullBathrooms`}
        mode="light"
        label="Full bathrooms*"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.numFullBathrooms) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={3}
        type="select"
        options={constructionTypeOptions}
        id={`${basePath}.building.constructionType`}
        name={`${basePath}.building.constructionType`}
        mode="light"
        label="Construction type*"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.constructionType) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={4}
        type="numeric"
        id={`${basePath}.building.numFireplaces`}
        name={`${basePath}.building.numFireplaces`}
        mode="light"
        label="Fireplaces*"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.numFireplaces) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={4}
        type="numeric"
        id={`${basePath}.building.numWoodBurningStoves`}
        name={`${basePath}.building.numWoodBurningStoves`}
        mode="light"
        label="Wood stoves*"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.numWoodBurningStoves) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={4}
        type="select"
        options={basementTypeOptions}
        id={`${basePath}.building.basementType`}
        name={`${basePath}.building.basementType`}
        mode="light"
        label="Basement/Foundation*"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.basementType) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={12}
        type="select"
        options={exteriorWallTypeOptions}
        id={`${basePath}.building.exteriorWallType`}
        name={`${basePath}.building.exteriorWallType`}
        mode="light"
        label="Exterior wall finish*"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.exteriorWallType) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={4}
        type="numeric"
        id={`${basePath}.building.garageCarCapacity`}
        name={`${basePath}.building.garageCarCapacity`}
        mode="light"
        label="Garage's car space*"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.garageCarCapacity) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={4}
        type="select"
        options={garageTypeOptions}
        id={`${basePath}.building.garageType`}
        name={`${basePath}.building.garageType`}
        mode="light"
        label="Garage type*"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.garageType) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={4}
        type="numeric"
        id={`${basePath}.building.attachedStructureSqFt`}
        name={`${basePath}.building.attachedStructureSqFt`}
        mode="light"
        label="Attached garage sq ft*"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.attachedStructureSqFt) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={4}
        type="numeric"
        id={`${basePath}.building.roofYear`}
        name={`${basePath}.building.roofYear`}
        mode="light"
        label="Roof year*"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.roofYear) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={4}
        id={`${basePath}.building.roofType`}
        name={`${basePath}.building.roofType`}
        type="select"
        options={roofTypeOptions}
        mode="light"
        label="Roof type*"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.roofType) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={4}
        id={`${basePath}.building.roofShape`}
        name={`${basePath}.building.roofShape`}
        type="select"
        options={roofShapeOptions}
        mode="light"
        label="Roof shape*"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.roofShape) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={6}
        id={`${basePath}.building.primaryHeatingSource`}
        name={`${basePath}.building.primaryHeatingSource`}
        type="select"
        options={heatingSourceOptions}
        mode="light"
        label="Primary heating*"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.primaryHeatingSource) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={6}
        id={`${basePath}.building.secondaryHeatingSource`}
        name={`${basePath}.building.secondaryHeatingSource`}
        type="select"
        options={heatingSourceOptions}
        mode="light"
        label="Secondary heat"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.secondaryHeatingSource) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={6}
        type="numeric"
        id={`${basePath}.building.numSecondaryHeatingSources`}
        name={`${basePath}.building.numSecondaryHeatingSources`}
        mode="light"
        label="Heating units"
        disabled={disableRiskForm || !isSelectedSecondaryHeatingSourceUnitCountBased}
        onChange={(val) => {
          if (val !== initialBuilding.numSecondaryHeatingSources) {
            toastRCEWarning?.();
          }
        }}
      />
      <Grid item xs={12}>
        <LabelTooltip
          label="Solid Fuel-Burning Appliances*"
          tooltip={{
            label: 'More Info',
            onHoverText:
              'Solid Fuel or wood heat examples include standard fireplace, fireplace with insert, indoor solid fuel or wood heat (including pellet stoves), outside solid fuel or wood heat. Barrel or Homemade stoves are Not Acceptable.'
          }}
          mode="light"
        >
          <Field
            type="numeric"
            id={`${basePath}.building.numSolidFuelBurningAppliances`}
            name={`${basePath}.building.numSolidFuelBurningAppliances`}
            mode="light"
            format={{
              allowNegative: false
            }}
            disabled={disableRiskForm}
            onChange={(val) => {
              if (val !== initialBuilding?.numSolidFuelBurningAppliances) {
                toastRCEWarning?.();
              }
            }}
          />
        </LabelTooltip>
      </Grid>
      <RenovationFieldsForm basePath={basePath} disabled={disableRiskForm} onFieldChange={() => toastRCEWarning?.()} />
      <Field
        xs={6}
        id={`${basePath}.connectedHome.theftPreventionDeviceType`}
        name={`${basePath}.connectedHome.theftPreventionDeviceType`}
        type="select"
        options={theftPreventionDevicesOptions}
        mode="light"
        label="Theft protective device"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialConnectedHome?.theftPreventionDeviceType) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={6}
        id={`${basePath}.connectedHome.fireProtectiveDeviceType`}
        name={`${basePath}.connectedHome.fireProtectiveDeviceType`}
        options={fireProtectiveDevicesOptions}
        type="select"
        mode="light"
        label="Fire protective device"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialConnectedHome?.fireProtectiveDeviceType) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={6}
        name={`${basePath}.connectedHome.internetMonitoring`}
        id={`${basePath}.connectedHome.internetMonitoring`}
        type="checkbox"
        label="Internet connected monitoring device(s)?"
        mode="light"
        disabled={disableRiskForm}
        className={classes.checkboxContainer}
        onChange={(val) => {
          if (val !== initialConnectedHome?.internetMonitoring) {
            toastRCEWarning?.();
          }
        }}
      />
    </>
  );
};

BarndomininumForm.propTypes = {
  basePath: PropTypes.string.isRequired,
  isSelectedSecondaryHeatingSourceUnitCountBased: PropTypes.bool,
  disableRiskForm: PropTypes.bool,
  initialBuilding: PropTypes.object,
  initialConnectedHome: PropTypes.object,
  toastRCEWarning: PropTypes.func,
  setFieldValue: PropTypes.func
};

export default BarndomininumForm;
